import { FC, useMemo } from 'react';
import { Divider, Stack, Typography, Tooltip } from '@mui/material';
import {
  ActionIconLocation,
  ActionIconSpeechBubble,
} from '@vs-online-only-app/ui';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import { useConversationShopperLocation } from '../../hooks/useConversationShopperLocation';

const MAX_DISPLAYED_LANGS = 3;

const SelectedConversationShopperLanguageAndLocation: FC = () => {
  const conversation = useSelectedHeroConversation();
  const { formattedAddress } = useConversationShopperLocation(conversation);

  const shopperLanguages = useMemo(() => {
    return conversation?.conversationStarter?.languages
      ?.map((locale) =>
        new Intl.DisplayNames(['en'], { type: 'language' }).of(locale)
      )
      .sort();
  }, [conversation]);

  const languageCount = shopperLanguages?.length || 0;
  const extraLanguageCount = languageCount - MAX_DISPLAYED_LANGS;
  const hasExtraLanguages = languageCount > MAX_DISPLAYED_LANGS;

  return (
    <Stack gap={1.5} direction="row">
      {formattedAddress && (
        <>
          <Stack
            gap={1}
            alignItems="center"
            direction="row"
            flexWrap="nowrap"
            maxWidth="30%"
          >
            <Tooltip title="Shopper's location" arrow>
              <div>
                <ActionIconLocation />
              </div>
            </Tooltip>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {formattedAddress}
            </Typography>
          </Stack>
          <Divider orientation="vertical" />
        </>
      )}
      {shopperLanguages && (
        <Stack alignItems="center" direction="row">
          <Stack gap={1} alignItems="center" direction="row" flexWrap="nowrap">
            <Tooltip title="Shopper's languages" arrow>
              <div>
                <ActionIconSpeechBubble />
              </div>
            </Tooltip>
            <Tooltip
              title={hasExtraLanguages ? shopperLanguages.join(', ') : ''}
            >
              <Typography variant="body2" color="textSecondary">
                {shopperLanguages.slice(0, MAX_DISPLAYED_LANGS).join(', ')}
                {hasExtraLanguages && ` + ${extraLanguageCount} more`}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default SelectedConversationShopperLanguageAndLocation;
